import { LibraryCache } from 'base/library_cache';
import { getNodeName } from 'base/webflow/data_attributes';
import { Component, NestedNode } from 'base/webflow/schema';
import { findElementReplacableTextContent } from 'blocks/editor/ai/element_replacable_text_content';
import {
  ElementInstance,
  ElementInstanceState,
  OptionalNodeIdCommentAnchoringState,
  SlotOverrideState,
} from 'blocks/editor/model';
import { elementTreeDfs } from 'blocks/editor/model_helpers';
import { recordMap } from 'blocks/woot_woot/string_map';
import { UndoManager } from 'blocks/woot_woot/undo';
import { findSlotsIn } from './slots';

// Components that we should check for invalid slot overrides, and reset
// elements for if found. They could potentially be in that state because we've
// published backwards-incompatible changes to their slot/element structure in
// the past.
const CHECK_INVALID_COMPONENT_SLUGS = new Set([
  'cookie1',
  'cookie2',
  'cookie3',
  'cookie4',
  'cookie5',
  'section_event37',
  'section_multi-form1',
  'section_multi-form2',
  'section_multi-form3',
  'section_multi-form4',
  'section_multi-form5',
  'section_multi-form6',
  'section_multi-form7',
  'section_multi-form8',
  'section_multi-form9',
  'section_multi-form10',
  'section_multi-form24',
  'section_multi-form25',
  'section_multi-form26',
  'section_multi-form27',
  'section_multi-form28',
  'section_multi-form29',
  'section_multi-form30',
  'section_multi-form31',
  'section_multi-form32',
  'section_multi-form33',
]);

export const syncSlotOverrides = ({
  element: el,
  library,
  undo,
  checkInvalidComponentSlugs = CHECK_INVALID_COMPONENT_SLUGS,
}: {
  element: ElementInstance;
  library: LibraryCache;
  undo: Pick<UndoManager, 'doPermanently'>;
  // Allow overriding for testing
  checkInvalidComponentSlugs?: Set<string>;
}) => {
  const comp = library.getCachedPublicComponentJson(el.source.slug)?.value;
  if (!comp) return;

  // If this is a root element of a component that we've published
  // backwards-incompatible changes to in the past, check for invalid slot
  // overrides. If found, reset its elements to prevent errors when trying to
  // render the component.
  if (
    checkInvalidComponentSlugs.has(el.source.slug) &&
    el.source.rootName === undefined
  ) {
    if (hasInvalidSlotOverrides(el, comp)) {
      removeSlotOverrides(el);
    }
  }

  const slotInfos = findSlotsIn(el.source, comp);
  for (const info of slotInfos) {
    if (!el.slotOverrides.has(info.name)) {
      undo.doPermanently(() => {
        el.slotOverrides.set(info.name, {
          items: info.originalItems.map((item) => ({
            ...item.state,
            ...pushDownElementState(item, el, comp),
          })),
        });
      });
    }

    for (const childEl of el.slotOverrides.get(info.name)!.items) {
      syncSlotOverrides({
        element: childEl,
        library,
        undo,
      });
    }
  }

  const validSlotNames = slotInfos.map((info) => info.name);
  for (const name of el.slotOverrides.keys()) {
    if (!validSlotNames.includes(name)) {
      console.warn(
        `ElementInstance contains slot name ${name} but webflow json only contains`,
        validSlotNames,
        el.state,
      );
    }
  }

  // If the element has exactly 1 text node, its text selection is merged
  // with the element selection.  So we have to move the comment anchor.
  // I'm sorry.
  // This is totally the wrong place to fix this issue - commenting
  // should be smarter about rendering comments on non-selectable nodes.
  const text = findElementReplacableTextContent(el, library);
  if (text.length === 1) {
    undo.doPermanently(() => {
      for (const [_, anchoring] of el.commentThreads.entries()) {
        anchoring.nodeId = undefined;
      }
    });
  }
};

const pushDownElementState = (
  to: { state: ElementInstanceState; rootNode: NestedNode },
  parent: ElementInstance,
  comp: Component,
) => {
  if (parent.source.slug !== to.state.source.slug) {
    throw new Error(
      `Can't push down text from ${to.state.source.slug} to ${parent.source.slug} (different slugs not supported)`,
    );
  }

  const textOverrides = new Map<string, string>();
  const commentThreads = new Map<string, OptionalNodeIdCommentAnchoringState>();

  function visit(node: NestedNode) {
    const text = parent.textOverrides.get(node._id);
    if (text != null) {
      textOverrides.set(node._id, text);
      parent.textOverrides.delete(node._id);
    }
    for (const [threadId, anchoring] of parent.commentThreads.entries()) {
      if (anchoring.nodeId === node._id) {
        commentThreads.set(threadId, anchoring.state);
        parent.commentThreads.delete(threadId);
      }
    }
    for (const child of node.children) {
      visit(child);
    }
  }
  visit(to.rootNode);

  const slotOverrides = new Map<string, SlotOverrideState>();

  const slotInfos = findSlotsIn(to.state.source, comp);
  for (const info of slotInfos) {
    const override = parent.slotOverrides.get(info.name);
    if (override) {
      slotOverrides.set(info.name, override.state);
      parent.slotOverrides.delete(info.name);
    }
  }

  return {
    textOverrides: recordMap(textOverrides),
    commentThreads: recordMap(commentThreads),
    slotOverrides: recordMap(slotOverrides),
  };
};

/**
 * Clears slot overrides from the element. Tries to preserve text overrides from
 * the descendants by moving them to the root element.
 */
const removeSlotOverrides = (rootElement: ElementInstance) => {
  function visit(el: ElementInstance) {
    for (const [nodeId, text] of el.textOverrides.entries()) {
      rootElement.textOverrides.set(nodeId, text);
    }
    for (const { items } of el.slotOverrides.values()) {
      for (const child of items) {
        visit(child);
      }
    }
  }
  visit(rootElement);

  rootElement.slotOverrides.clear();
};

/**
 * Checks that the names of the element and all its descendants point to actual
 * nodes in the component. Returns true if any are missing, indicating that we
 * won't be able to render the component in the site builder.
 */
const hasInvalidSlotOverrides = (el: ElementInstance, comp: Component) => {
  // Check that the names of all elements in the hierarchy point to actual
  // nodes in the component. If any are missing, we won't be able to render
  // the element.
  const { nodes } = comp.payload;
  const validNodeNames = new Set(nodes.map(getNodeName));
  for (const child of elementTreeDfs(el)) {
    if (child.source.slug !== el.source.slug) {
      continue;
    }
    if (!validNodeNames.has(child.source.rootName)) {
      return true;
    }
  }
  return false;
};
